<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { isManual, newsParams } from '@/lib/news'
import { useTrackTitle } from '@/composables/useTrackTitle'

/**
 * The text of a news (see doc/news.md). An automatic news is a template with the names in it, and the
 * player and track names are links — to the profile and to the track page — so a name in the news is
 * worth the same as a name anywhere else on the site. A manual news is its own text, no links.
 */
const props = defineProps({
  news: { type: Object, required: true },
  /** true = the whole text (the dialog), false = the one-line title (the lists). */
  body: { type: Boolean, default: false },
})

const { t } = useI18n()
const { fullName } = useTrackTitle()

const keypath = computed(() => `news.auto.${props.news.rule}`)
const params = computed(() => newsParams(props.news, t, fullName))
</script>

<template>
  <template v-if="isManual(news)">
    {{ body ? (news.body ?? news.title ?? '') : (news.title ?? '') }}
  </template>

  <i18n-t v-else :keypath="keypath" tag="span" scope="global">
    <template #playerName>
      <router-link
        v-if="params.playerName"
        class="news-link"
        :to="{ name: 'player', params: { username: params.playerName } }"
        @click.stop
      >{{ params.playerName }}</router-link>
      <span v-else>–</span>
    </template>

    <template #winnerName>
      <router-link
        v-if="params.winnerName"
        class="news-link"
        :to="{ name: 'player', params: { username: params.winnerName } }"
        @click.stop
      >{{ params.winnerName }}</router-link>
      <span v-else>–</span>
    </template>

    <template #trackLabel>
      <router-link
        v-if="params.trackKey"
        class="news-link"
        :to="{ name: 'track', params: { trackId: params.trackKey } }"
        @click.stop
      >{{ params.trackLabel }}</router-link>
      <span v-else>–</span>
    </template>

    <template #eventTypeLabel>
      <router-link
        v-if="params.eventId"
        class="news-link"
        :to="{ name: 'event_detail', params: { id: params.eventId } }"
        @click.stop
      >{{ params.eventTypeLabel }}</router-link>
      <span v-else>{{ params.eventTypeLabel }}</span>
    </template>
    <template #period>{{ params.period }}</template>
  </i18n-t>
</template>

<style scoped>
/* In the colour of the context and underlined only on hover, like a player or track name elsewhere:
   a list of news stays readable instead of becoming a wall of blue. */
.news-link {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted currentColor;
}
.news-link:hover {
  color: rgb(var(--v-theme-primary));
  border-bottom-color: transparent;
}
</style>
